/**
 * What a workspace's git sync looks like right now — one place that turns the
 * stored status (store/gitSyncStore.js) into the words and tone the Git page,
 * the rail dot and the status bar all show (git sync Stage 1, card 7).
 *
 * Apiboo terms, not git terms (owner, 28.09.2026: "not a git client"):
 * "Synced · 2 min ago", "Changes waiting", "3 updates on the remote",
 * "Sync failed".
 */
import { useGitSyncStore, recordGitSyncSuccess, recordGitSyncError } from '../../store/gitSyncStore';
import { dequeue, enqueue } from './offlineQueue';
import { changesWaitingOf, isGitTracked, isLocalOnly, isRemoteLinked, setChangesWaitingReader } from './gitTracking';
import { VAULT_LOCKED_SYNC_MESSAGE } from '../vaultGate';
import i18n from '../../i18n';

// The predicates live in gitTracking.js (pure, no store — the MCP server reads
// them); re-exported so every existing import keeps working.
export { isGitTracked, isLocalOnly, isRemoteLinked };

// The MCP workspace report asks "changes waiting?" through this reader, so it
// never imports the store: one boolean (or null) per workspace, nothing else.
setChangesWaitingReader((workspace) =>
  changesWaitingOf(useGitSyncStore.getState().byWorkspace?.[String(workspace?.id)], { local: isLocalOnly(workspace) })
);

/**
 * The words a workspace's git actions use: a local history SAVES versions,
 * a remote link SYNCS ("not a git client", owner 28.09.2026).
 */
const REMOTE_WORDS = Object.freeze({
  local: false,
  action: 'Sync now',
  last: 'the last sync',
  nothingWaiting: 'Nothing waiting — everything is synced',
  noHistory: 'No syncs yet — the first Sync now starts the history.',
  nextRecords: 'the next sync records it as a new change',
  itemNotYet: (noun) => `Not synced yet — the next Sync now starts this ${noun}'s history.`,
  itemUnsaved: (noun) => `This ${noun} has changes that are not synced yet.`,
  itemNone: (noun) => `No syncs of this ${noun} yet.`,
  busy: 'Syncing — the history updates when it is done.',
});
const LOCAL_WORDS = Object.freeze({
  local: true,
  action: 'Save version',
  last: 'the last saved version',
  nothingWaiting: 'Nothing new — everything is saved',
  noHistory: 'No versions yet — Save version starts the history.',
  nextRecords: 'the next Save version records it as a new change',
  itemNotYet: (noun) => `Not saved yet — the next Save version starts this ${noun}'s history.`,
  itemUnsaved: (noun) => `This ${noun} has changes that are not saved yet.`,
  itemNone: (noun) => `No saved versions of this ${noun} yet.`,
  busy: 'Saving — the history updates when it is done.',
});

export function gitWords(workspace) {
  return isLocalOnly(workspace) ? LOCAL_WORDS : REMOTE_WORDS;
}

/** Read `git_status` (after a best-effort fetch; none for a local link) into the store. Never throws. */
export async function refreshGitStatus(workspace) {
  if (!isGitTracked(workspace)) return null;
  try {
    // Loaded on use: the rail imports this module, and the sync service's
    // graph (fs, events, the flush) must not come with it.
    const { gitStatus } = await import('../workspaceGitSync');
    const status = await gitStatus(workspace);
    useGitSyncStore.getState().setStatus(String(workspace.id), status || null);
    return status;
  } catch {
    return null;
  }
}

/**
 * A sync finished (push, pull, connect, resolve): remember the outcome and
 * re-read the status in the background. Called by the handlers in useWorkspaces.
 */
export function noteGitSync(workspace, { ok = false, error, queued = false } = {}) {
  if (!workspace?.id) return;
  if (queued) {
    // A local history has nothing to send — it is never queued.
    if (isLocalOnly(workspace)) return;
    // Committed here; the push waits for the network (set 7d).
    enqueue(workspace.id);
    useGitSyncStore.getState().recordQueued(String(workspace.id));
    return;
  }
  // A sync that ended — either way — no longer waits in the queue.
  dequeue(workspace.id);
  if (ok) recordGitSyncSuccess(workspace.id);
  else recordGitSyncError(workspace.id, error);
  refreshGitStatus(workspace);
}

/** "just now" / "2 min ago" / "3 h ago" / "4 d ago". */
export function ago(ms, now = Date.now()) {
  if (!ms) return '';
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

/** describeSync for a workspace tracked only on this device (T1, T7). */
function describeLocal(entry, { syncing, now }) {
  if (syncing) return { tone: 'busy', label: 'Saving…' };
  if (entry?.lastError) return { tone: 'error', label: 'Save failed' };
  const st = entry?.status;
  // The mirror's .git is gone (deleted by hand, a cleaner…): the next Save
  // version starts a fresh history and says so (T7).
  if (st && st.initialized === false) return { tone: 'error', label: 'History missing on this device' };
  if (st?.hasLocalChanges) {
    const n = entry?.changeCount;
    return { tone: 'waiting', label: n > 0 ? `${n} change${n === 1 ? '' : 's'} not saved yet` : 'Changes not saved yet' };
  }
  if (entry?.lastSyncAt) return { tone: 'ok', label: `Saved on this device · ${ago(entry.lastSyncAt, now)}` };
  if (st && !st.headOid) return { tone: 'idle', label: 'Not saved yet' };
  if (st) return { tone: 'ok', label: 'Saved on this device' };
  return { tone: 'idle', label: 'Not checked yet' };
}

/**
 * @returns {{ tone: 'ok'|'waiting'|'error'|'busy'|'idle', label: string, vaultLocked?: boolean }}
 *   tone drives the colour and the rail dot (waiting → dot, error → red dot).
 *   `local`: the workspace is tracked only on this device — "Saved on this
 *   device · 2 min ago" / "3 changes not saved yet" instead of sync words.
 */
export function describeSync(entry, { syncing = false, now = Date.now(), local = false } = {}) {
  if (local) return describeLocal(entry, { syncing, now });
  if (syncing) return { tone: 'busy', label: 'Syncing…' };
  if (entry?.queued) return { tone: 'waiting', label: 'Waiting to send · offline' };
  // The token lives in the Vault and the Vault is locked: not a failure, and
  // never a prompt from the background — a quiet state whose click unlocks.
  if (entry?.status?.vaultLocked || entry?.lastError === VAULT_LOCKED_SYNC_MESSAGE) {
    return { tone: 'idle', label: i18n.t('gitSync.unlockVaultToSync', VAULT_LOCKED_SYNC_MESSAGE), vaultLocked: true };
  }
  if (entry?.lastError) return { tone: 'error', label: 'Sync failed' };
  const st = entry?.status;
  const waiting = Boolean(st?.hasLocalChanges) || (st?.ahead || 0) > 0;
  if (waiting) {
    const n = entry?.changeCount;
    return { tone: 'waiting', label: n > 0 ? `${n} change${n === 1 ? '' : 's'} waiting` : 'Changes waiting' };
  }
  if ((st?.behind || 0) > 0) {
    const n = st.behind;
    return { tone: 'waiting', label: `${n} update${n === 1 ? '' : 's'} on the remote` };
  }
  if (entry?.lastSyncAt) return { tone: 'ok', label: `Synced · ${ago(entry.lastSyncAt, now)}` };
  if (st) return { tone: 'ok', label: 'Up to date' };
  return { tone: 'idle', label: 'Not checked yet' };
}

/**
 * The rail's mark for all linked workspaces: 'error' beats 'waiting' beats
 * nothing. A local history only ever adds the red error dot — unsaved edits
 * are normal work, not something waiting to go out.
 */
export function railMark(workspaces, byWorkspace, linkedScopes = {}) {
  let mark = null;
  const tracked = [
    ...(workspaces || []).filter(isGitTracked).map((ws) => [String(ws.id), isLocalOnly(ws)]),
    // Projects, boards & documents count too (owner, 06.10.2026: a board change showed no dot).
    ...Object.entries(linkedScopes || {}).map(([key, s]) => [key, Boolean(s?.local)]),
  ];
  for (const [id, local] of tracked) {
    const { tone } = describeSync(byWorkspace?.[id], { local });
    if (tone === 'error') return 'error';
    if (tone === 'waiting' && !local) mark = 'waiting';
  }
  return mark;
}
